import React, { useState } from "react";
import {
  Box,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import MicOffIcon from "@mui/icons-material/MicOff";
import MicIcon from "@mui/icons-material/Mic";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import VideocamIcon from "@mui/icons-material/Videocam";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare";

interface BatchCapabilityControlsProps {
  meetingId: string;
  onBatchUpdate: (
    operation: "mute_all" | "unmute_all" | "disable_video_all" | "enable_video_all" | "disable_content_all" | "enable_content_all",
    excludeHosts: boolean,
    lock: boolean
  ) => Promise<void>;
  disabled?: boolean;
  // Track current lock states for all attendees
  isAudioLocked?: boolean;
  isVideoLocked?: boolean;
  isContentLocked?: boolean;
}

const BatchCapabilityControls: React.FC<BatchCapabilityControlsProps> = ({
  onBatchUpdate,
  disabled = false,
  isAudioLocked = false,
  isVideoLocked = false,
  isContentLocked = false,
}) => {
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    capability: "audio" | "video" | "content";
    operation: "mute_all" | "unmute_all" | "disable_video_all" | "enable_video_all" | "disable_content_all" | "enable_content_all";
    willLock: boolean;
  } | null>(null);
  const [excludeHosts, setExcludeHosts] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleCapabilityClick = (capability: "audio" | "video" | "content") => {
    let operation: "mute_all" | "unmute_all" | "disable_video_all" | "enable_video_all" | "disable_content_all" | "enable_content_all";
    let willLock: boolean;

    if (capability === "audio") {
      willLock = !isAudioLocked;
      operation = willLock ? "mute_all" : "unmute_all";
    } else if (capability === "video") {
      willLock = !isVideoLocked;
      operation = willLock ? "disable_video_all" : "enable_video_all";
    } else {
      willLock = !isContentLocked;
      operation = willLock ? "disable_content_all" : "enable_content_all";
    }

    setPendingAction({ capability, operation, willLock });
    setConfirmDialogOpen(true);
  };

  const handleConfirm = async () => {
    if (!pendingAction) return;

    setLoading(true);
    try {
      await onBatchUpdate(pendingAction.operation, excludeHosts, true);
    } catch (error) {
      console.error("Failed to update capability", error);
    } finally {
      setLoading(false);
      setConfirmDialogOpen(false);
      setPendingAction(null);
    }
  };

  const handleCancel = () => {
    setConfirmDialogOpen(false);
    setPendingAction(null);
  };

  const getCapabilityLabel = (capability: "audio" | "video" | "content") => {
    switch (capability) {
      case "audio": return "Audio";
      case "video": return "Video";
      case "content": return "Screen Share";
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {/* Audio Toggle */}
      <Tooltip title={isAudioLocked ? "Unlock Audio for All" : "Lock Audio for All"}>
        <IconButton
          size="small"
          onClick={() => handleCapabilityClick("audio")}
          disabled={disabled || loading}
          color={isAudioLocked ? "error" : "default"}
        >
          {isAudioLocked ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {/* Video Toggle */}
      <Tooltip title={isVideoLocked ? "Unlock Video for All" : "Lock Video for All"}>
        <IconButton
          size="small"
          onClick={() => handleCapabilityClick("video")}
          disabled={disabled || loading}
          color={isVideoLocked ? "error" : "default"}
        >
          {isVideoLocked ? <VideocamOffIcon fontSize="small" /> : <VideocamIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {/* Content Share Toggle */}
      <Tooltip title={isContentLocked ? "Unlock Screen Share for All" : "Lock Screen Share for All"}>
        <IconButton
          size="small"
          onClick={() => handleCapabilityClick("content")}
          disabled={disabled || loading}
          color={isContentLocked ? "error" : "default"}
        >
          {isContentLocked ? <StopScreenShareIcon fontSize="small" /> : <ScreenShareIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={handleCancel}>
        <DialogTitle>
          {pendingAction?.willLock ? "Lock" : "Unlock"} {pendingAction && getCapabilityLabel(pendingAction.capability)} for All
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {pendingAction?.willLock
              ? `This will disable ${getCapabilityLabel(pendingAction.capability).toLowerCase()} for all attendees and prevent them from enabling it.`
              : `This will allow all attendees to control their own ${getCapabilityLabel(pendingAction?.capability || "audio").toLowerCase()}.`
            }
          </DialogContentText>
          {/* Only show exclude hosts option when locking - unlock always affects everyone */}
          {pendingAction?.willLock && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={excludeHosts}
                  onChange={(e) => setExcludeHosts(e.target.checked)}
                  size="small"
                />
              }
              label="Exclude hosts from this action"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color={pendingAction?.willLock ? "error" : "primary"}
            variant="contained"
            disabled={loading}
          >
            {loading ? "Updating..." : (pendingAction?.willLock ? "Lock" : "Unlock")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchCapabilityControls;
